import { amountToNumber } from '../../../../util/shared/vue-global';

/**
 * Drives the tax-details grid's own Tax Yr / Tax ID row lookup from the fields in the
 * paymentDialog header.
 *
 * This lives outside dialogTaxDetails on purpose - that component is being retired, so nothing
 * new is added to it. Only its existing methods (addOne, preUpdateEditedItem) are called from
 * here, which means the load, the status-code / mortgage-code / no-taxes-due / pending-payment
 * popups and the duplicate-row dialog all behave exactly as they do when the clerk keys the pair
 * into the row itself - none of that is reimplemented.
 *
 * When dialogTaxDetails goes, this file goes with it: the header then routes only to
 * LargePaymentList (addTaxRowByYearAndId).
 */

/**
 * A row nothing was ever entered on - the one a new payment opens with, and the one left behind
 * after a load. Same predicate dialogTaxDetails.dropBlankPaymentRows uses.
 */
export function isBlankTaxRow(row) {
  return !!row && row.itsNewRecord && !row.posted && !row.void
    && !row.taxId && !row.taxInquiryId
    && !amountToNumber(row.taxAmt) && !amountToNumber(row.total)
    && !(row.fees || []).length;
}

/**
 * Stages the keyed pair on a blank row and hands it to the grid's lookup.
 *
 * @param {String|Number} taxYear        keyed tax year
 * @param {String|Number} taxId          keyed tax id (or parcel id - the grid decides by length)
 * @param {String}   taxType             payment type keyed in the band's Type selector, staged
 *                                       on the row alongside the pair so the grid's own lookup
 *                                       keeps it (addOne seeds every new row as TAXPAYMENT)
 * @param {Object}   gridRef             the dialogTaxDetails component instance ($refs)
 * @param {Function} getTaxDetails       reads the payment's current taxDetails array
 * @param {Function} setTaxDetails       writes the array back onto the payment
 * @param {Function} nextTick            the caller's $nextTick
 * @returns {Promise<Boolean>} whether the pair was staged, so the header can clear its fields
 */
export async function loadTaxRowViaTaxDetailsGrid({
  taxYear, taxId, taxType, gridRef, getTaxDetails, setTaxDetails, nextTick,
}) {
  if (!taxYear || !taxId || !gridRef) return false;
  if (typeof gridRef.preUpdateEditedItem !== 'function') return false;

  let rows = [...(getTaxDetails() || [])];
  let index = rows.findIndex(isBlankTaxRow);
  // no blank row to take - ask the grid for one, exactly as the '+' button does
  if (index === -1) {
    if (typeof gridRef.addOne !== 'function') return false;
    gridRef.addOne();
    // addOne emits its rows up, so re-read rather than reusing the stale snapshot
    await nextTick();
    rows = [...(getTaxDetails() || [])];
    index = rows.length - 1;
  }
  if (index < 0) return false;

  rows[index] = {
    ...rows[index],
    taxYear: amountToNumber(taxYear),
    taxId,
    // a type was keyed in the band - otherwise the row keeps the TAXPAYMENT addOne gave it
    taxType: taxType || rows[index].taxType,
  };
  setTaxDetails(rows);
  // the staged pair has to come back down as the grid's prop before its lookup reads it
  await nextTick();
  const staged = (getTaxDetails() || [])[index];
  if (!staged) return false;
  await gridRef.preUpdateEditedItem(staged, index, true);
  return true;
}

/**
 * dialogTaxDetails answers its own warning popups internally - Continue / Cancel are its methods
 * and it emits nothing when they run - so there is no event to listen for. This watches its
 * popup flags from out here and reports the moment the clerk has answered them all, which is
 * when the cursor can go back to the header.
 *
 * Reaching into the child's state rather than adding an emit to it is deliberate: that component
 * is being retired, so nothing is added there. One-shot, and disposed either way, so a later
 * popup can never fire a stale callback.
 *
 * @param {Object}   gridRef        the dialogTaxDetails instance ($refs)
 * @param {Function} watch          the caller's $watch, as (getter, cb) => unwatch
 * @param {Function} onAnswered     called once, after an open popup closes
 * @param {Number}   giveUpAfterMs  drop the watch if no popup was ever raised (a clean lookup)
 * @returns {Function} disposer - safe to call more than once
 */
export function onWarningPopupsAnswered({
  gridRef, watch, onAnswered, giveUpAfterMs = 8000,
}) {
  const noop = () => {};
  if (!gridRef || typeof watch !== 'function') return noop;
  const anyOpen = () => !!(gridRef.showOrHidePopUpForAllMsg
    || gridRef.showPendingPaymentPopup
    || gridRef.duplicateTaxDetailDialog);

  let sawOpen = false;
  let disposed = false;
  let unwatch = noop;
  let timerId = null;
  const dispose = () => {
    if (disposed) return;
    disposed = true;
    clearTimeout(timerId);
    unwatch();
  };

  unwatch = watch(anyOpen, (open) => {
    if (open) {
      // a popup is up - the clerk decides when this ends, so stop the give-up timer
      sawOpen = true;
      clearTimeout(timerId);
      return;
    }
    if (!sawOpen) return;
    dispose();
    onAnswered();
  });
  // the lookup has a debounce and an API call in front of it, so allow real time for a popup to
  // appear; if none does, the lookup was clean and there is nothing to wait for
  timerId = setTimeout(dispose, giveUpAfterMs);
  return dispose;
}

export default { isBlankTaxRow, loadTaxRowViaTaxDetailsGrid, onWarningPopupsAnswered };
